import { Grid, styled } from '@mui/material';
import TruncatedTypographicText from 'src/components/truncated-typographic-text';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { formatNumber } from '../../../pricing-simulator';
import { toggleDrawerView } from '../../../planner_v2/store/planner-slice';
import DRAWER_VIEW_TYPE from '../../../planner_v2/types/drawer-view-type';
import { IProductMetric } from '../../../planner_v2/types/ppg-sku-drawer.model';
import MetricsPopper from './metrics-popper';
import usePopper from '../../../planner_v2/hooks/popper-hook';
import {
  IColorGradientMapDetails,
  ToBeMetric,
  ToBeMetricGradient
} from '../../types/grid-color-gradient-map.model';

interface IProductProps {
  /**
   * Name of the product
   */
  name: string[];
  /**
   * view to be passed to the drawer (PPG or SKU)
   */
  view: DRAWER_VIEW_TYPE;
  /**
   * The id of the product passed to drawer when opened
   */
  productId: number;
  /**
   * Is the product marked for opportunities
   */
  isMarkedForOpportunity: boolean;
  /**
   * Gradient value nto define the gradient opacity
   */
  // eslint-disable-next-line react/no-unused-prop-types
  gradientValue?: number;
}

interface IGridDatumBoxProps extends IProductProps {
  /**
   * Details of the price of the product
   */
  price: IProductMetric[];
  /**
   * Opportunities selected for the product
   */
  opportunities: number[];
  /**
   * Metrics of the product
   */
  metrics: IProductMetric[];

  // eslint-disable-next-line react/require-default-props
  metricColors?: ToBeMetricGradient[];
  /**
   * Image of the product
   */
  imgUrl: string;
  /**
   * If the item is hover-able. Defaults to false
   */
  isHover: boolean;
  /**
   * If the item is clickable. (Does drawer open on clicking?)
   */
  isClickable: boolean;
  /**
   * Metric value to decide the intensity of color (Only for TO BE Grid)
   */
  metricValue: number;
  /**
   * Max value of the metric
   */
  metricMaxValue: number;
  /**
   * Min value of the metric
   */
  metricMinValue: number;
  /**
   * Key name to pointing to the product name/id etc
   */
  productKeyRef: string;
  /**
   * Is opportunity grid visible
   */
  isOpportunityVisible: boolean;
}

const StyledDiv = styled('div')(() => ({
  borderRadius: '50%',
  backgroundSize: 'cover',
  height: '30px',
  width: '30px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  border: '0.72px solid #8080CF'
}));

/**
 * Renders the image for the PPG
 */
function ItemImage({
  price,
  imgUrl
}: {
  price: IGridDatumBoxProps['price'];
  imgUrl: string;
}) {
  return (
    <Grid item xs='auto'>
      <Grid>
        <StyledDiv
          sx={{
            border: '0.72px solid #8080CF',
            backgroundColor: 'white',
            borderRadius: '50%'
          }}
          style={{
            backgroundImage: `url(${
              imgUrl || '/static/images/logo/apple-touch-icon-120x120.png'
            })`
          }}
        />
      </Grid>
      {price.map((p) => (
        <Grid
          key={p.alias}
          sx={{
            backgroundColor: '#E6E6F6',
            color: '#8080CF',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '8px',
            borderRadius: (theme) => theme.spacing(1),
            padding: (theme) => theme.spacing(0.25, 1)
          }}
        >
          {formatNumber(p.metric_format, p.value, p.prefix, p.suffix)}
        </Grid>
      ))}
    </Grid>
  );
}

function ItemOpportunityGrid({ opportunities }: { opportunities: number[] }) {
  const { opportunityToColorMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  return (
    <Grid
      item
      sx={{
        width: '10px',
        borderRadius: '5px',
        backgroundColor: '#E8E8E8',
        mx: 1,
        height: '50px',
        overflow: 'hidden'
      }}
    >
      {Array.isArray(opportunities) && opportunities.length
        ? opportunities.map((opportunityId) => (
            <Grid
              key={opportunityId}
              sx={{
                backgroundColor:
                  opportunityToColorMap.find(
                    (opp) =>
                      opp.pk_id === opportunityId ||
                      opp.intermediate_opp_id === opportunityId
                  )?.color || '#E8E8E8',
                height: `${(100 / opportunities.length / 100) * 50}px`
              }}
            />
          ))
        : null}
    </Grid>
  );
}

/**
 * Renders the image and name of the product (PPG) in grid cell
 */
export default function ItemDetails({
  productId,
  view,
  price,
  name,
  opportunities,
  metrics,
  metricColors = [],
  metricValue,
  metricMaxValue,
  metricMinValue,
  imgUrl,
  isOpportunityVisible = false,
  productKeyRef = '',
  isHover = false,
  isClickable = false,
  isMarkedForOpportunity = false
}: IGridDatumBoxProps) {
  const appDispatch = useAppDispatch();
  const { handleMouseEnter, handleMouseLeave, anchorEl, isPopperOpen } =
    usePopper({ showPopper: productId > -1 });
  const { opportunityToColorMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  const delisOpp = opportunityToColorMap.find((opp) => opp.value === 'delist');
  return (
    <Grid flexWrap='nowrap' mb={1} item xs={12} container>
      <Grid item xs='auto'>
        <ItemImage
          price={price}
          imgUrl={imgUrl || '/static/images/logo/apple-touch-icon-120x120.png'}
        />
      </Grid>
      {isOpportunityVisible ? (
        <ItemOpportunityGrid opportunities={opportunities} />
      ) : null}
      <Grid
        item
        xs
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={
          metricColors.length > 2
            ? {
                backgroundColor:
                  metricValue > 0
                    ? `rgba(0, 178, 47, ${metricValue / metricMaxValue})`
                    : metricValue < 0
                    ? `rgba(255, 134, 131, ${metricValue / metricMinValue})`
                    : metricValue === 0 &&
                      opportunities.includes(delisOpp?.pk_id)
                    ? 'rgba(255, 134, 131, 1)'
                    : 'transparent',
                borderRadius: 1,
                padding: '4px'
              }
            : {
                backgroundColor: 'transparent',
                borderRadius: 1,
                padding: '4px'
              }
        }
      >
        <TruncatedTypographicText
          fontSize='10px'
          fontWeight={!isMarkedForOpportunity ? 400 : 700}
          textTransform='uppercase'
          color={isMarkedForOpportunity || metricValue ? 'black' : '#777777'}
          sx={
            isHover
              ? {
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: (theme) => theme.palette.primary.main
                  }
                }
              : {}
          }
          onClick={() => {
            if (isClickable) {
              appDispatch(
                toggleDrawerView({
                  view: DRAWER_VIEW_TYPE.PRODUCT_DETAILS,
                  productId,
                  productKeyRef
                })
              );
            }
          }}
        >
          {name}
        </TruncatedTypographicText>
      </Grid>
      {isHover && metrics.length ? (
        <MetricsPopper
          metrics={metrics}
          open={isPopperOpen}
          anchorEl={anchorEl}
        />
      ) : null}
    </Grid>
  );
}
